/**
 * The feature feedback board's vocabulary, shared by the Worker and the
 * browser so the two can never disagree about what a status means.
 *
 * Two vocabularies live here rather than one, deliberately. The INTERNAL
 * statuses are the lifecycle the product team works in — ten of them, most of
 * which a customer has no reason to distinguish. The CUSTOMER-FACING labels
 * are the four tabs and the handful of badges a cleaner actually reads. The
 * map between them is code, not a column, so the wording on the board can
 * change without rewriting anybody's row.
 */

// ---------------------------------------------------------------------------
// Lifecycle (§16)
// ---------------------------------------------------------------------------

export const FEEDBACK_STATUSES = [
  /** Submitted, waiting for a moderator. Visible to its submitter only. */
  'pending_moderation',
  /** Published on the board. The resting state of an idea nobody has judged yet. */
  'approved',
  'under_consideration',
  'planned',
  'in_progress',
  'preview',
  'deployed',
  'rejected',
  /** Folded into another request, which now carries its votes and followers. */
  'merged',
  /** Taken off the board without being rejected — stale, superseded, withdrawn. */
  'archived',
] as const;

export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

/**
 * Statuses a customer can see on somebody else's request. Everything else is
 * either private to its submitter (pending), or gone from the board (rejected,
 * merged, archived).
 */
export const FEEDBACK_PUBLIC_STATUSES: readonly FeedbackStatus[] = [
  'approved',
  'under_consideration',
  'planned',
  'in_progress',
  'preview',
  'deployed',
];

/**
 * Statuses that count as a live, votable idea — what "approved active feature"
 * means in §15, and what the monthly participation requirement is satisfied by
 * a vote on. A deployed feature keeps its votes but is no longer asking for
 * them, so voting on it is refused rather than silently uncounted.
 */
export const FEEDBACK_VOTABLE_STATUSES: readonly FeedbackStatus[] = [
  'approved',
  'under_consideration',
  'planned',
  'in_progress',
  'preview',
];

/**
 * The four customer tabs (§6), and which internal statuses fall under each.
 *
 * `under_consideration` and `planned` stay in Ideas with a badge rather than
 * getting tabs of their own — the spec is explicit about it, and a five-tab
 * board is exactly the complexity this feature is meant to avoid.
 */
export const FEEDBACK_TABS = ['ideas', 'in_progress', 'preview', 'completed'] as const;
export type FeedbackTab = (typeof FEEDBACK_TABS)[number];

export const FEEDBACK_TAB_STATUSES: Readonly<Record<FeedbackTab, readonly FeedbackStatus[]>> = {
  ideas: ['approved', 'under_consideration', 'planned'],
  in_progress: ['in_progress'],
  preview: ['preview'],
  completed: ['deployed'],
};

/**
 * What a customer reads instead of the internal name. Keys the translation
 * files use as well, so `feedback.status.<key>` is the one place the wording
 * lives per language.
 */
export const FEEDBACK_CUSTOMER_STATUS: Readonly<Record<FeedbackStatus, string>> = {
  pending_moderation: 'pending',
  approved: 'idea',
  under_consideration: 'under_consideration',
  planned: 'planned',
  in_progress: 'in_progress',
  preview: 'preview',
  deployed: 'completed',
  rejected: 'rejected',
  merged: 'merged',
  archived: 'archived',
};

/**
 * Which status may follow which, enforced server-side on every write (§44).
 *
 * The shape of this table is the answer to "a normal user must never be able
 * to call an endpoint that changes approved → deployed": the endpoint that
 * moves a status is operator-only to begin with, and even for an operator the
 * move has to be one this table allows. Rejection and merging are reachable
 * from anywhere still on the board, because a duplicate is often only spotted
 * after an idea has been live for a month.
 */
export const FEEDBACK_STATUS_TRANSITIONS: Readonly<Record<FeedbackStatus, readonly FeedbackStatus[]>> = {
  pending_moderation: ['approved', 'rejected', 'merged'],
  approved: ['under_consideration', 'planned', 'in_progress', 'preview', 'deployed', 'rejected', 'merged', 'archived'],
  under_consideration: ['approved', 'planned', 'in_progress', 'preview', 'deployed', 'rejected', 'merged', 'archived'],
  planned: ['under_consideration', 'in_progress', 'preview', 'deployed', 'rejected', 'merged', 'archived'],
  in_progress: ['planned', 'preview', 'deployed', 'archived', 'merged'],
  preview: ['in_progress', 'deployed', 'archived', 'merged'],
  // Shipping is where the lifecycle ends. A feature released by mistake is
  // corrected by shipping again, not by moving the request backwards — the
  // release notes and the completion date on it are a public record.
  deployed: [],
  rejected: ['approved', 'merged'],
  // A merged request is a pointer now. Unmerging is a separate admin action
  // that clears `canonical_request_id`, not a status move.
  merged: [],
  archived: ['approved', 'planned', 'in_progress'],
};

export function canTransitionFeedback(from: FeedbackStatus, to: FeedbackStatus): boolean {
  return FEEDBACK_STATUS_TRANSITIONS[from].includes(to);
}

// ---------------------------------------------------------------------------
// Product areas (§9)
// ---------------------------------------------------------------------------

export const FEEDBACK_CATEGORIES = [
  'scheduling',
  'customers',
  'quotes',
  'magic_quote',
  'payments',
  'invoices',
  'messaging',
  'employees',
  'mobile_app',
  'reporting',
  'integrations',
  'other',
] as const;

export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number];

// ---------------------------------------------------------------------------
// Moderation (§12)
// ---------------------------------------------------------------------------

export const FEEDBACK_REJECTION_REASONS = [
  'duplicate',
  'support_question',
  'bug_report',
  'spam',
  'already_exists',
  'already_supported',
  'not_aligned',
  'insufficient_information',
  'other',
] as const;

export type FeedbackRejectionReason = (typeof FEEDBACK_REJECTION_REASONS)[number];

export const FEEDBACK_MODERATION_ACTIONS = ['approve', 'edit_approve', 'merge', 'reject', 'unmerge', 'status'] as const;
export type FeedbackModerationAction = (typeof FEEDBACK_MODERATION_ACTIONS)[number];

// ---------------------------------------------------------------------------
// Sorting (§7)
// ---------------------------------------------------------------------------

export const FEEDBACK_SORTS = ['trending', 'votes', 'newest', 'updated'] as const;
export type FeedbackSort = (typeof FEEDBACK_SORTS)[number];

/** The window "recent votes" means, for both trending and the admin columns. */
export const FEEDBACK_TRENDING_WINDOW_DAYS = 7;

// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------

export const FEEDBACK_TITLE_MAX = 120;
export const FEEDBACK_DESCRIPTION_MIN = 20;
export const FEEDBACK_DESCRIPTION_MAX = 5_000;
export const FEEDBACK_PREVIEW_FEEDBACK_MAX = 2_000;

/** Defaults for every §38 setting, applied when the directory row is missing. */
export const FEEDBACK_DEFAULTS = {
  enabled: true,
  weeklySubmissionLimit: 3,
  voteRequirementEnabled: true,
  requiredVotesPerMonth: 1,
  newCompanyGraceDays: 30,
  moderationRequired: true,
  previewEnabled: true,
  previewSessionMinutes: 1_440,
  aiDuplicateDetectionEnabled: true,
} as const;

// ---------------------------------------------------------------------------
// Eligibility (§41)
// ---------------------------------------------------------------------------

/**
 * Why a company cannot submit right now. `null` is the answer when it can.
 *
 * `feature_disabled` and `permission` are here beside the two quota reasons so
 * the front end has one field to switch on rather than a quota object plus a
 * scatter of special cases.
 */
export const FEEDBACK_INELIGIBILITY_REASONS = [
  'feature_disabled',
  'permission',
  'monthly_vote_required',
  'weekly_limit_reached',
] as const;

export type FeedbackIneligibilityReason = (typeof FEEDBACK_INELIGIBILITY_REASONS)[number];

export interface FeedbackEligibility {
  canSubmit: boolean;
  reason: FeedbackIneligibilityReason | null;
  weeklyLimit: number;
  weeklyUsed: number;
  weeklyRemaining: number;
  votesRequiredThisMonth: number;
  qualifyingVotesThisMonth: number;
  /** Null once the grace period has passed, or when the requirement is off. */
  graceEndsAt: string | null;
}

// ---------------------------------------------------------------------------
// Duplicate detection (§8, §31)
// ---------------------------------------------------------------------------

/**
 * Words dropped before a title is compared with another one.
 *
 * Short and English-only on purpose. This list exists to stop "add a way to
 * automatically remind customers" and "automatically remind customers" from
 * looking like different ideas; it is not a stemmer and does not pretend to be.
 * A Spanish-speaking customer's title normalizes to its own words unchanged,
 * which costs a little recall on the similarity pass and nothing else — the
 * moderator still sees the candidates, and the keyword overlap below still
 * scores them.
 */
const TITLE_STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'be',
  'can',
  'could',
  'for',
  'from',
  'have',
  'in',
  'into',
  'is',
  'it',
  'me',
  'my',
  'of',
  'on',
  'or',
  'our',
  'please',
  'should',
  'that',
  'the',
  'their',
  'them',
  'they',
  'this',
  'to',
  'us',
  'we',
  'when',
  'with',
  'would',
  'you',
  'your',
]);

/**
 * The comparable form of a title: lowercased, accents folded, punctuation
 * dropped, stop words removed, remaining words sorted and joined.
 *
 * Sorting the words is what makes "reminders for inactive customers" and
 * "inactive customer reminders" normalize alike — word order carries almost no
 * meaning in a feature title, and treating it as significant is how a board
 * fills up with the same idea written four ways.
 */
export function normalizeFeedbackTitle(title: string): string {
  const words = title
    .normalize('NFKD')
    // Combining marks, so "reactivación" folds to "reactivacion".
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length > 0)
    // Plural "s" is the one inflection worth folding by hand: "reminders" and
    // "reminder" are the same request, and nothing else in English is this
    // cheap to get right.
    .map((word) => (word.length > 3 && word.endsWith('s') && !word.endsWith('ss') ? word.slice(0, -1) : word))
    .filter((word) => !TITLE_STOP_WORDS.has(word));

  return [...new Set(words)].sort().join(' ');
}

/** The distinct significant words of a title, for the overlap score below. */
export function feedbackTitleKeywords(title: string): string[] {
  const normalized = normalizeFeedbackTitle(title);
  return normalized.length === 0 ? [] : normalized.split(' ');
}

/**
 * How alike two titles are, 0–100.
 *
 * Jaccard overlap of their significant words: shared words over total distinct
 * words. Crude, fast, and explainable to a moderator — which matters more than
 * accuracy here, because the number is shown beside a candidate rather than
 * acted on. Nothing is auto-merged on the strength of it.
 */
export function feedbackTitleSimilarity(a: string, b: string): number {
  const left = new Set(feedbackTitleKeywords(a));
  const right = new Set(feedbackTitleKeywords(b));
  if (left.size === 0 || right.size === 0) return 0;

  let shared = 0;
  for (const word of left) if (right.has(word)) shared += 1;
  const union = left.size + right.size - shared;
  return union === 0 ? 0 : Math.round((shared / union) * 100);
}

// ---------------------------------------------------------------------------
// Trending (§7)
// ---------------------------------------------------------------------------

/**
 * The trending score, computed in TypeScript rather than in SQL so it can
 * change without a migration — which is exactly what §7 asks for.
 *
 * Recent votes dominate, total votes contribute with diminishing returns, and
 * age pulls everything down gently. The shape matters more than the constants:
 * an idea that collected forty votes last spring should not sit above one
 * collecting ten this week, and an idea nobody has voted for in six months
 * should drift off the front page without anybody archiving it.
 */
export function feedbackTrendingScore(input: {
  totalVotes: number;
  recentVotes: number;
  createdAt: string;
  lastVoteAt: string | null;
  now?: Date;
}): number {
  const now = input.now ?? new Date();
  const ageDays = Math.max(0, (now.getTime() - Date.parse(input.createdAt)) / 86_400_000);
  const quietDays = input.lastVoteAt
    ? Math.max(0, (now.getTime() - Date.parse(input.lastVoteAt)) / 86_400_000)
    : ageDays;

  // Diminishing returns on the standing total: the difference between 5 and 25
  // votes should matter far more than the difference between 200 and 220.
  const standing = Math.log1p(Math.max(0, input.totalVotes)) * 10;
  const momentum = Math.max(0, input.recentVotes) * 25;
  // Half-life of roughly a month on silence, and a gentler one on raw age so a
  // genuinely popular old idea is demoted rather than buried.
  const quietDecay = Math.pow(0.5, quietDays / 30);
  const ageDecay = Math.pow(0.5, ageDays / 365);

  return Number(((momentum + standing) * quietDecay * ageDecay).toFixed(4));
}
